import formatNumber from './format-number';

/**
 * @typedef {'staff'|'newsletters'|'limitAutomations'|'customThemes'|'customIntegrations'|'members'|'limitAnalytics'|'limitStripeConnect'} LimitName
 */

/**
 * @template {LimitName} T
 * @typedef {object} ProductOrPriceLimit - as returned from Daisy API for current site usage
 * @property {T} name - The name of the limit
 * @property {T extends 'staff' ? number :
 *            T extends 'newsletters' ? number :
 *            T extends 'limitAutomations' ? number :
 *            T extends 'customThemes' ? ('official' | string) :
 *            T extends 'customIntegrations' ? boolean :
 *            T extends 'members' ? number :
 *            T extends 'limitAnalytics' ? boolean :
 *            T extends 'limitStripeConnect' ? boolean :
 *            never} value - The limit value
 */

/**
 * @typedef {{exceeded: boolean}} BaseLimit
 * @typedef {BaseLimit & {total: number, limit: number}} NumericLimit
 * @typedef {NumericLimit & {members_override?: number}} MembersLimit
 * @typedef {BaseLimit & {active: string|null, hasOfficialTheme: boolean, limit: string[]}} AllowListLimit
 * @typedef {BaseLimit & {active: boolean|null, limit: boolean|null}} FlagLimit
 *
 * @typedef {{
 *   [K in LimitName]: K extends 'staff' ? NumericLimit :
 *                     K extends 'newsletters' ? NumericLimit :
 *                     K extends 'limitAutomations' ? NumericLimit :
 *                     K extends 'customThemes' ? AllowListLimit :
 *                     K extends 'customIntegrations' ? FlagLimit :
 *                     K extends 'members' ? MembersLimit :
 *                     K extends 'limitAnalytics' ? FlagLimit :
 *                     K extends 'limitStripeConnect' ? FlagLimit :
 *                     never
 * }} SiteLimits
 */

/**
 * @typedef {object} LimitValidation
 * @property {string} limit - The limit name
 * @property {boolean} valid - Whether the limit is valid
 * @property {string|null} message - Validation message if invalid
 */

/**
 * Formats a theme name for display by capitalizing each word
 * Converts hyphenated theme names to title case with spaces
 * @param {string} themeName - The theme name to format (e.g., "news-room", "casper-1.4")
 * @returns {string} Formatted theme name (e.g., "News Room", "Casper 1.4")
 * @example
 * formatThemeName('news-room') // returns 'News Room'
 * formatThemeName('casper') // returns 'Casper'
 */
const formatThemeName = (themeName) => {
    return themeName
        .split('-')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
};

/**
 * Formats multiple theme names for display with proper grammar
 * Handles comma-separated lists and adds "or" for readability
 * @param {string} themeNames - Comma-separated theme names (e.g., "casper, source")
 * @returns {string} Formatted theme names with proper separators (e.g., "Casper or Source")
 * @example
 * formatThemeNames('casper') // returns 'Casper'
 * formatThemeNames('casper, source') // returns 'Casper or Source'
 * formatThemeNames('casper, source, headline') // returns 'Casper, Source or Headline'
 */
const formatThemeNames = (themeNames) => {
    const themes = themeNames.split(',').map((name) => formatThemeName(name.trim()));
    if (themes.length === 1) {
        return themes[0];
    }
    if (themes.length === 2) {
        return `${themes[0]} or ${themes[1]}`;
    }
    // For 3 or more themes: "Theme1, Theme2, Theme3 or Theme4"
    return themes.slice(0, -1).join(', ') + ' or ' + themes[themes.length - 1];
};

/**
 * Creates a standardized validation result for theme limit checks
 * Generates appropriate error messages based on which themes are required
 * @param {string} limitName - The limit name (typically 'customThemes')
 * @param {boolean} isValid - Whether the customer's theme is compatible with the product
 * @param {string|null} productThemeRequirement - The product's theme requirement(s) for error messages
 * @returns {LimitValidation} Validation result with appropriate message
 * @example
 * createThemeValidationResult('customThemes', false, 'casper')
 * // returns {limit: 'customThemes', valid: false, message: 'Only available with Casper theme installed'}
 */
const createThemeValidationResult = (limitName, isValid, productThemeRequirement = null) => {
    if (isValid) {
        return { limit: limitName, valid: true, message: null };
    }

    const message = productThemeRequirement
        ? `Only available with ${formatThemeNames(productThemeRequirement)} theme installed`
        : 'Not available when using custom theme';

    return { limit: limitName, valid: false, message };
};

/**
 * Checks if a limit value represents a truthy/enabled state
 * Handles various truthy representations from the API
 * @param {*} value - The limit value to check
 * @returns {boolean} Whether the limit is enabled
 */
const isTruthyLimitValue = (value) => {
    return value === true || value === 'true' || value === '1';
};

/**
 * Validates staff limit for a product
 * Business products bypass staff limits
 * @param {ProductOrPriceLimit} productStaffLimit - Product's staff limit requirement
 * @param {NumericLimit} currentStaffUsage - Current site's staff usage
 * @param {string} productName - Product name
 * @returns {LimitValidation} Validation result
 */
const validateStaffLimit = (productStaffLimit, currentStaffUsage, productName) => {
    const isBusinessProduct = productName.toLowerCase() === 'business';
    const isWithinLimit = currentStaffUsage.total <= productStaffLimit.value;

    return {
        limit: productStaffLimit.name,
        valid: isBusinessProduct || isWithinLimit,
        message:
            !isBusinessProduct && !isWithinLimit
                ? `Not available with more than ${formatNumber(productStaffLimit.value)} staff ${productStaffLimit.value > 1 ? 'users' : 'user'}`
                : null,
    };
};

/**
 * Validates newsletters limit
 * @param {ProductOrPriceLimit} productNewslettersLimit - Product's newsletters limit requirement
 * @param {NumericLimit} currentNewslettersUsage - Current site's newsletter usage
 * @returns {LimitValidation} Validation result
 */
const validateNewslettersLimit = (productNewslettersLimit, currentNewslettersUsage) => {
    const isWithinLimit = currentNewslettersUsage.total <= productNewslettersLimit.value;

    return {
        limit: productNewslettersLimit.name,
        valid: isWithinLimit,
        message: !isWithinLimit
            ? productNewslettersLimit.value === 1
                ? 'Not available with multiple newsletters'
                : `Not available with more than ${formatNumber(productNewslettersLimit.value)} ${productNewslettersLimit.value > 1 ? 'newsletters' : 'newsletter'}`
            : null,
    };
};

/**
 * Validates automations limit
 * @param {ProductOrPriceLimit<'limitAutomations'>} productAutomationsLimit - Product's automation allowance
 * @param {NumericLimit} currentAutomationsUsage - Current site's automation usage
 * @returns {LimitValidation} Validation result
 */
const validateLimitAutomations = (productAutomationsLimit, currentAutomationsUsage) => {
    const isWithinLimit = currentAutomationsUsage.total <= productAutomationsLimit.value;

    return {
        limit: productAutomationsLimit.name,
        valid: isWithinLimit,
        message: !isWithinLimit
            ? productAutomationsLimit.value === 1
                ? 'Not available with multiple automations'
                : `Not available with more than ${formatNumber(productAutomationsLimit.value)} automations`
            : null,
    };
};

/**
 * Validates custom themes limit
 * @param {ProductOrPriceLimit} productThemesLimit - Product's theme requirement
 * @param {AllowListLimit} currentThemeConfig - Current site's theme configuration
 * @returns {LimitValidation} Validation result
 */
const validateCustomThemesLimit = (productThemesLimit, currentThemeConfig) => {
    if (typeof productThemesLimit.value !== 'string') {
        return {
            limit: productThemesLimit.name,
            valid: true,
            message: null,
        };
    }

    // Product's theme requirement (e.g., "official", "casper", or "casper, source")
    // Remove quotes and normalize to lowercase for consistent comparison
    const requiredThemes = productThemesLimit.value.replace(/["']/g, '').trim().toLowerCase();

    // Check if product requires any official Ghost theme
    if (requiredThemes.includes('official')) {
        return createThemeValidationResult(
            productThemesLimit.name,
            currentThemeConfig.hasOfficialTheme,
        );
    }

    // Product requires specific theme(s) - parse comma-separated list
    const allowedThemes = requiredThemes.split(',').map((t) => t.trim());
    const activeTheme = currentThemeConfig.active
        ? currentThemeConfig.active.trim().toLowerCase()
        : null;

    // Validate: Is the customer's currently active theme allowed by this product?
    const themeIsCompatible = activeTheme && allowedThemes.includes(activeTheme);

    return createThemeValidationResult(productThemesLimit.name, themeIsCompatible, requiredThemes);
};

/**
 * Validates custom integrations limit
 * @param {ProductOrPriceLimit} productIntegrationsLimit - Product's integrations limit requirement
 * @returns {LimitValidation} Validation result
 */
const validateCustomIntegrationsLimit = (productIntegrationsLimit) => {
    // TODO: implement actual validation when Daisy returns custom integrations limit
    return {
        limit: productIntegrationsLimit.name,
        valid: true,
        message: null,
    };
};

/**
 * Validates members limit
 * @param {ProductOrPriceLimit} productMembersLimit - Product's members limit requirement
 * @param {MembersLimit} currentMembersCount - Current site's member count
 * @returns {LimitValidation} Validation result
 */
const validateMembersLimit = (productMembersLimit, currentMembersCount) => {
    const isWithinLimit = currentMembersCount.total <= productMembersLimit.value;

    return {
        limit: productMembersLimit.name,
        valid: isWithinLimit,
        message: !isWithinLimit
            ? `Not available with more than ${formatNumber(productMembersLimit.value)} members`
            : null,
    };
};

/**
 * Validates analytics limit
 * @param {ProductOrPriceLimit} productAnalyticsLimit - Product's analytics limit flag
 * @param {FlagLimit} currentAnalyticsStatus - Current site's analytics configuration
 * @returns {LimitValidation} Validation result
 */
const validateLimitAnalytics = (productAnalyticsLimit, currentAnalyticsStatus) => {
    const productHasLimit = isTruthyLimitValue(productAnalyticsLimit.value);
    const analyticsActive = currentAnalyticsStatus.active === true;
    const analyticsLimited = currentAnalyticsStatus.limit === true;
    const bothLimited = analyticsActive && analyticsLimited && productHasLimit;

    // Scenarios:
    // 1. Analytics is not active (any product can be used)
    // 2. Analytics is active but new product doesn't have the limit (upgrade)
    // 3. Analytics is active, both products have the limit (blocked-to-blocked)
    const productCompatible = !analyticsActive || !productHasLimit || bothLimited;

    return {
        limit: productAnalyticsLimit.name,
        valid: productCompatible,
        message: !productCompatible ? 'Not available with Web Analytics enabled' : null,
    };
};

/**
 * Validates Stripe Connect limit
 * @param {ProductOrPriceLimit} productStripeConnectLimit - Product's Stripe Connect limit flag
 * @param {FlagLimit} currentStripeConnectStatus - Current site's Stripe Connect configuration
 * @returns {LimitValidation} Validation result
 */
const validateLimitStripeConnect = (productStripeConnectLimit, currentStripeConnectStatus) => {
    const productHasLimit = isTruthyLimitValue(productStripeConnectLimit.value);
    const stripeConnectActive = currentStripeConnectStatus?.active === true;

    // Valid scenarios:
    // 1. Stripe Connect not active - any plan works
    // 2. New plan has no Stripe Connect limit - upgrade allowed
    // If Stripe Connect is active and new plan has limit - not allowed
    const productCompatible = !stripeConnectActive || !productHasLimit;

    return {
        limit: productStripeConnectLimit.name,
        valid: productCompatible,
        message: !productCompatible ? 'Not available with Stripe Connect enabled' : null,
    };
};

/**
 * Check limits of passed in product and price limits against current site usage to determine if
 * the passed in product or price is suitable for the customer.
 * @private
 * @param {ProductOrPriceLimit[]} productOrPriceLimits - Array of product or price limits to check
 * @param {SiteLimits} currentSiteLimits - Currently applied site limits
 * @param {string} [productNameToCheck] - Product name - omitted when checking price limits
 * @returns {LimitValidation[]} Array of validation results
 */
const _checkLimits = (productOrPriceLimits, currentSiteLimits, productNameToCheck = '') => {
    let validationResults = [];

    productOrPriceLimits.forEach((productOrPriceLimit) => {
        // Skip if limit is malformed or not present
        if (
            !productOrPriceLimit ||
            !productOrPriceLimit.name ||
            productOrPriceLimit.value === undefined ||
            productOrPriceLimit.value === null
        ) {
            return;
        }

        if (currentSiteLimits[productOrPriceLimit.name]) {
            const currentSiteLimit = currentSiteLimits[productOrPriceLimit.name];

            // compare allowed limit of product or price and current usage
            switch (productOrPriceLimit.name) {
                case 'staff':
                    validationResults.push(
                        validateStaffLimit(
                            productOrPriceLimit,
                            currentSiteLimit,
                            productNameToCheck,
                        ),
                    );
                    break;
                case 'newsletters':
                    validationResults.push(
                        validateNewslettersLimit(productOrPriceLimit, currentSiteLimit),
                    );
                    break;
                case 'limitAutomations':
                    validationResults.push(
                        validateLimitAutomations(productOrPriceLimit, currentSiteLimit),
                    );
                    break;
                case 'customThemes':
                    validationResults.push(
                        validateCustomThemesLimit(productOrPriceLimit, currentSiteLimit),
                    );
                    break;
                case 'customIntegrations':
                    validationResults.push(validateCustomIntegrationsLimit(productOrPriceLimit));
                    break;
                case 'members':
                    validationResults.push(
                        validateMembersLimit(productOrPriceLimit, currentSiteLimit),
                    );
                    break;
                case 'limitAnalytics':
                    validationResults.push(
                        validateLimitAnalytics(productOrPriceLimit, currentSiteLimit),
                    );
                    break;
                case 'limitStripeConnect':
                    validationResults.push(
                        validateLimitStripeConnect(productOrPriceLimit, currentSiteLimit),
                    );
                    break;
                default:
                    break;
            }
        }
    });

    return validationResults;
};

/**
 * Check if a price/product combination is valid based on current site limits
 * @param {object} price - Price object containing limits
 * @param {ProductOrPriceLimit[]} [price.limits] - Array of price-specific limits
 * @param {object} options - Options object
 * @param {ProductOrPriceLimit[]} [options.productLimits] - Array of product-specific limits
 * @param {SiteLimits} [options.currentSiteLimits] - Current site limits
 * @param {string} [options.product] - Product name
 * @returns {LimitValidation[]} Array of invalid limits with validation messages
 */
const checkLimits = (price, { productLimits, currentSiteLimits, product } = {}) => {
    /** @type {LimitValidation[]} */
    let productValidations = [];
    /** @type {LimitValidation[]} */
    let priceValidations = [];

    if (!currentSiteLimits) {
        throw new Error('currentSiteLimits missing');
    }

    // check product limits (staff, customThemes, customIntegrations)
    if (productLimits && productLimits.length) {
        productValidations = _checkLimits(productLimits, currentSiteLimits, product);
    }

    // check price limits (members)
    if (price && price.limits && price.limits.length) {
        priceValidations = _checkLimits(price.limits, currentSiteLimits);
    }

    const allValidations = productValidations.concat(priceValidations);

    const invalidLimits = allValidations.filter(
        (validation) => validation.valid === false && validation.message,
    );

    return invalidLimits;
};

export default checkLimits;
export {
    formatThemeName,
    formatThemeNames,
    createThemeValidationResult,
    isTruthyLimitValue,
    validateStaffLimit,
    validateNewslettersLimit,
    validateLimitAutomations,
    validateCustomThemesLimit,
    validateCustomIntegrationsLimit,
    validateMembersLimit,
    validateLimitAnalytics,
    validateLimitStripeConnect,
};
